import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { fitView, intrinsicSize, isVectorSource, zoomView } from "./imageFit";

// How long after the last zoom step the vector is re-drawn at its new size.
// While a wheel is still turning the last drawing is stretched, which is
// cheap; the moment it stops, the picture is re-drawn razor sharp.
const SETTLE_MS = 140;

// Pan and zoom for image diagrams (SVG, PNG, JPG …). Rendered through <img>,
// so SVG markup from the database can't run scripts - nothing here ever puts
// that markup into the document.
//
// A vector is scaled by giving the element a real width and height, so the
// browser re-draws the drawing itself at display size. A bitmap keeps the CSS
// transform, which is all its pixels can offer anyway.
const ImageCanvas = forwardRef(function ImageCanvas({ src, alt, vector, markup }, ref) {
  const isVector = vector === undefined ? isVectorSource(src) : Boolean(vector);
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const settle = useRef(null);
  const [size, setSize] = useState(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  // What the picture was last drawn at. At rest it equals the zoom, so the
  // element's own size is the size on screen and a vector is pixel-perfect.
  const [drawnAt, setDrawnAt] = useState(1);
  // The live view, so a wheel turned twice before React re-renders still
  // zooms from where the picture actually is.
  const viewRef = useRef(view);
  viewRef.current = view;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const apply = useCallback((next, { redraw = false } = {}) => {
    viewRef.current = next;
    setView(next);
    if (redraw) {
      clearTimeout(settle.current);
      setDrawnAt(next.k);
      return;
    }
    clearTimeout(settle.current);
    settle.current = setTimeout(() => setDrawnAt(next.k), SETTLE_MS);
  }, []);

  useEffect(() => () => clearTimeout(settle.current), []);

  // A new picture: nothing known about it until it loads.
  useEffect(() => {
    setSize(null);
    sizeRef.current = null;
  }, [src]);

  const fitTo = useCallback(
    (measured) => {
      const box = boxRef.current;
      if (!box || !measured) return;
      const next = fitView({
        boxWidth: box.clientWidth,
        boxHeight: box.clientHeight,
        width: measured.width,
        height: measured.height,
        vector: isVector,
      });
      if (next) apply(next, { redraw: true });
    },
    [apply, isVector]
  );

  const measure = useCallback(() => {
    const img = imgRef.current;
    const measured = intrinsicSize({
      src,
      markup,
      naturalWidth: img?.naturalWidth || 0,
      naturalHeight: img?.naturalHeight || 0,
      vector: isVector,
    });
    if (!measured) return null;
    sizeRef.current = measured;
    setSize(measured);
    return measured;
  }, [src, markup, isVector]);

  const fit = useCallback(() => fitTo(sizeRef.current || measure()), [fitTo, measure]);

  const zoomAt = useCallback((factor, cx, cy) => apply(zoomView(viewRef.current, factor, cx, cy)), [apply]);

  const zoomCentre = useCallback(
    (factor) => {
      const box = boxRef.current;
      if (box) zoomAt(factor, box.clientWidth / 2, box.clientHeight / 2);
    },
    [zoomAt]
  );

  useImperativeHandle(ref, () => ({ zoomIn: () => zoomCentre(1.25), zoomOut: () => zoomCentre(0.8), fit }), [
    zoomCentre,
    fit,
  ]);

  // A vector is laid out at its drawn size and only stretched between one
  // zoom step and the re-draw that follows it; a bitmap is transformed, which
  // is all a fixed grid of pixels can do.
  const style =
    isVector && size
      ? {
          width: `${size.width * drawnAt}px`,
          height: `${size.height * drawnAt}px`,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.k / drawnAt})`,
        }
      : {
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
          // A vector with no size yet would be laid out at whatever the
          // browser invents for it (it fills its container), which would show
          // for one frame as a picture the wrong size. It waits instead.
          ...(isVector ? { visibility: "hidden" } : null),
        };

  return (
    <div
      ref={boxRef}
      className="bf-image-canvas"
      onWheel={(e) => {
        const rect = boxRef.current.getBoundingClientRect();
        zoomAt(e.deltaY < 0 ? 1.1 : 0.9, e.clientX - rect.left, e.clientY - rect.top);
      }}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        drag.current = { x: e.clientX, y: e.clientY };
        const v = viewRef.current;
        apply({ ...v, x: v.x + dx, y: v.y + dy }, { redraw: true });
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        className={isVector ? "bf-image-canvas__sheet bf-image-canvas__sheet--vector" : "bf-image-canvas__sheet"}
        onLoad={() => fitTo(measure())}
        style={style}
      />
    </div>
  );
});

export default ImageCanvas;
